import { Component, OnDestroy, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Subscription } from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { AppState, CorpMemberState } from '../ngrx-store/app.state'
import { profileUpdateSuccess } from '../ngrx-store/actions/corp-member.actions'
import { CallerService } from '../services/caller.service'
import { environment } from '../../environments/environment'
import { onboardingBarVisible, snoozeMessage } from '../utils/onboarding.util'

/**
 * "Please verify your email" bar in NavComponent. There's no ✕ - we want them to verify - only
 * "Remind me later", which hides it until `email_verification_snoozed_until` (24h, stored on the
 * backend so it holds across devices). It waits while "Finish setting up" is showing.
 *
 * TODO: require a verified email for one high-value action (not decided yet) - see
 * snoozeEmailVerificationReminder in corpers.online.
 */
@Component({
    selector: 'app-email-verification-banner',
    templateUrl: './email-verification-banner.component.html',
    styleUrls: ['./email-verification-banner.component.css'],
})
export class EmailVerificationBannerComponent implements OnInit, OnDestroy {
    isVisible: boolean = false
    isResending: boolean = false
    isSnoozing: boolean = false

    private subscription: Subscription = new Subscription()

    constructor(
        private store: Store<AppState>,
        private callerService: CallerService,
    ) {}

    ngOnInit(): void {
        if (!environment.EMAIL_VERIFICATION_REMINDER_ENABLED) {
            return
        }

        this.subscription.add(
            this.store.pipe(select('corper')).subscribe((corper: CorpMemberState) => {
                const snoozedUntil = corper?.email_verification_snoozed_until
                const snoozed = !!snoozedUntil && new Date(snoozedUntil).getTime() > Date.now()
                // One bar at a time: wait while "Finish setting up" is showing - it's shown first.
                this.isVisible = Boolean(corper?.id) && !corper?.email_verified_at && !snoozed && !onboardingBarVisible(corper)
            }),
        )
    }

    remindLater(): void {
        this.isSnoozing = true
        this.callerService.snoozeVerificationReminder().subscribe({
            next: (res: any) => {
                this.isSnoozing = false
                this.store.dispatch(profileUpdateSuccess({ data: res.body.data }))
                this.callerService.showNotification(snoozeMessage(res?.body?.data?.email_verification_snoozed_until))
            },
            error: (err: HttpErrorResponse) => {
                this.isSnoozing = false
                this.callerService.showNotification(err?.error?.message ?? 'Couldn\'t snooze the reminder. Please try again.')
            },
        })
    }

    resend(): void {
        this.isResending = true

        this.callerService.resendVerificationEmail().subscribe({
            next: (res: any) => {
                this.isResending = false
                this.callerService.showNotification(res?.body?.message ?? 'Verification email sent.')
            },
            error: (err: HttpErrorResponse) => {
                this.isResending = false
                this.callerService.showNotification(err?.error?.message ?? 'Failed to resend verification email.')
            },
        })
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe()
    }
}
